import { faClose, faHistory } from "@fortawesome/sharp-light-svg-icons";
import type { UseMutationResult } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useFormState } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import type {
    Company,
    EmissionRecordView,
    EventFormInstance,
    Form,
} from "../../../../apiClient/generated";
import { ConfirmationDialog } from "../../../../ui/ConfirmationDialog";
import { useEventBus } from "../../../../utils/eventBus";
import { EventField } from "../../../EventsV2/EventFields";
import { useEventForm } from "../../../EventsV2/eventForm";
import { ValidationErrorSummary } from "../../../EventsV2/ValidationErrorSummary";
import { selectionContextAtom } from "../state";
import { EventFormActions } from "./FormActions";
import { FormPicker } from "./FormPicker";
import {
    EventFormHeader,
    FormGroupSection,
    FormNavigationPills,
    PillButton,
} from "./FormV2Ui";
import { FormV2ViewOnly } from "./FormV2ViewOnly";
import { EventTimeline } from "./TimelineHistory";

interface FormV2Props {
    emissionData: EmissionRecordView;
    company?: Company;
    availableForms: Form[];
    eventForms: EventFormInstance[];
    addFormToEventMutation: UseMutationResult<
        EventFormInstance,
        unknown,
        { formId: number }
    >;
    removeFormFromEventMutation: UseMutationResult<
        void,
        unknown,
        { eventFormInstanceId: number }
    >;
    onClose: () => void;
    onSaved?: () => void;
}

/**
 * FormV2 - used on site page.
 *
 * Renders forms/ui and manages closing side-panel
 */
export const FormV2 = (props: FormV2Props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [showHistory, setShowHistory] = useState(false);

    const enabledForms = useMemo(() => {
        return props.eventForms
            .map((eventFormInstance) => {
                return {
                    form: props.availableForms.find(
                        (f) => f.id === eventFormInstance.form,
                    ),
                    // If this is not included in default forms allow deleting it
                    remove: props.company?.defaultForms?.includes(
                        eventFormInstance.form,
                    )
                        ? undefined
                        : () =>
                              props.removeFormFromEventMutation.mutate({
                                  eventFormInstanceId: eventFormInstance.id,
                              }),
                };
            })
            .filter((f) => f.form);
    }, [
        props.eventForms,
        props.availableForms,
        props.company,
        props.removeFormFromEventMutation,
    ]);
    const formsEnabledForEvent = useMemo(
        () => enabledForms.map((i) => i.form),
        [enabledForms],
    );

    const { form, submit, formDefinition } = useEventForm(
        props.emissionData,
        props.company,
        formsEnabledForEvent,
        props.onSaved,
    );
    const { errors, touchedFields } = useFormState({
        control: form.control,
    });

    // Group names that contain at least one field with a validation error.
    // Not memoized on purpose: react-hook-form mutates the `errors` object
    // in place on setError() (e.g. server-side errors), so its reference is
    // not a reliable memo dependency.
    const groupsWithErrors = new Set(
        formDefinition
            .filter((group) =>
                group.fields.some((fieldDef) => {
                    if (!fieldDef.fieldName) return false;
                    const fieldError = fieldDef.fieldName
                        .split(".")
                        .reduce((current: any, key) => current?.[key], errors);
                    return fieldError != null;
                }),
            )
            .map((group) => group.groupName),
    );

    const [, setSelectionContext] = useAtom(selectionContextAtom);

    const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const scrollToGroup = (groupName: string) => {
        groupRefs.current[groupName]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    // Handle when form was touched and ask if user wants to leave.
    const [confirmClose, setConfirmClose] = useState(false);

    const restoreEmissionSelectionContext = () => {
        const emissionTimestamp = DateTime.fromJSDate(
            props.emissionData.dataPoint.detectionTimestamp,
        );
        setSelectionContext((s) => ({
            ...s,
            emissionId: props.emissionData.id,
            dateRange: {
                start: emissionTimestamp.minus({ days: 7 }).toJSDate(),
                end: emissionTimestamp.plus({ days: 7 }).toJSDate(),
            },
            highlightPosition: {
                date: emissionTimestamp.toJSDate(),
                x: 0,
            },
        }));
    };

    const requestCloseForm = () => {
        if (Object.values(touchedFields).some((i) => i)) {
            setConfirmClose(true);
            return;
        }
        props.onClose();
    };

    const handleConfirmClose = () => {
        setConfirmClose(false);
        props.onClose();
    };

    const handleCancelClose = () => {
        setConfirmClose(false);
        restoreEmissionSelectionContext();
    };

    useEventBus("sitePageEventFormClose", requestCloseForm);

    // If site changes, change site being displayed
    const siteOnForm = form.watch("site");
    const sitePage = searchParams.get("site");
    useEffect(() => {
        if (!touchedFields.site) return;
        if (siteOnForm == null) return;
        if (String(siteOnForm) === sitePage) return;
        if (
            confirm(
                "The site you selected is not the same being displayed in the page, do you want to switch over?",
            )
        ) {
            setSearchParams((prev) => {
                prev.set("site", String(siteOnForm));
                return prev;
            });
        }
    }, [siteOnForm, sitePage, setSearchParams, touchedFields.site]);

    const isEditable = !(
        props.emissionData.mainEvent.eventStatus === "COMPLETED" ||
        props.emissionData.mainEvent.eventStatus === "REJECTED" ||
        props.emissionData.mainEvent.eventStatus === "ARCHIVED"
    );

    if (!isEditable) {
        return (
            <FormV2ViewOnly
                emissionData={props.emissionData}
                company={props.company}
                availableForms={props.availableForms}
                eventForms={props.eventForms}
                onClose={props.onClose}
            />
        );
    }

    return (
        <FormProvider {...form}>
            <form
                onSubmit={submit}
                className="w-full h-full flex flex-col overflow-hidden"
            >
                <div className="flex-shrink-0">
                    <EventFormHeader
                        emissionRecord={props.emissionData}
                        requestCloseForm={requestCloseForm}
                        actions={
                            <EventFormActions
                                emission={props.emissionData}
                                onClose={requestCloseForm}
                                form={form}
                                submit={submit}
                            />
                        }
                    />
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto w-full">
                    {showHistory && (
                        <div className="">
                            <div className="flex items-center justify-between p-2 bg-neutral-50 border-b">
                                <h3 className="ml-1">Change history</h3>
                                <PillButton
                                    icon={faClose}
                                    label="Close History"
                                    onClick={() => setShowHistory(false)}
                                />
                            </div>
                            <div className="px-2">
                                <EventTimeline
                                    eventId={props.emissionData.mainEvent.id}
                                />
                            </div>
                        </div>
                    )}

                    {!showHistory && (
                        <>
                            <div className="flex items-center w-full justify-between px-2 py-2">
                                <FormPicker
                                    availableForms={props.availableForms}
                                    formsEnabledForEvent={formsEnabledForEvent}
                                    addFormToEventMutation={
                                        props.addFormToEventMutation
                                    }
                                    emissionRecord={props.emissionData}
                                />
                                <PillButton
                                    icon={faHistory}
                                    label="Show history"
                                    onClick={() => setShowHistory(true)}
                                />
                            </div>

                            <FormNavigationPills
                                forms={enabledForms}
                                groupsWithErrors={groupsWithErrors}
                                onSelectForm={scrollToGroup}
                            />

                            <div className="px-4">
                                <ValidationErrorSummary
                                    forms={formsEnabledForEvent}
                                    errors={errors}
                                />
                            </div>

                            <div className="flex flex-col gap-2 pb-2">
                                {formDefinition.map((group, groupIdx) => (
                                    <FormGroupSection
                                        key={group.groupName}
                                        group={group}
                                        index={groupIdx}
                                        defaultOpen={true}
                                        hasError={groupsWithErrors.has(
                                            group.groupName,
                                        )}
                                        sectionRef={(el) => {
                                            groupRefs.current[group.groupName] =
                                                el;
                                        }}
                                    >
                                        {group.fields.map(
                                            (fieldDef, fieldIdx) => (
                                                <EventField
                                                    key={
                                                        fieldDef.fieldName ??
                                                        fieldIdx
                                                    }
                                                    fieldDef={fieldDef}
                                                    emissionRecord={
                                                        props.emissionData
                                                    }
                                                />
                                            ),
                                        )}
                                    </FormGroupSection>
                                ))}
                            </div>
                        </>
                    )}
                    <hr />
                    <p className="p-4 text-sm text-neutral-500">
                        *: required fields.
                        <br />
                        **: conditionally required fields.
                    </p>
                </div>
            </form>
            <ConfirmationDialog
                visible={confirmClose}
                onClose={handleCancelClose}
                onConfirm={handleConfirmClose}
                title="Unsaved Changes"
                message="If you close the emission, the changes you've made will be lost. Are you sure you want to continue?"
                confirmText="Close Without Saving"
                cancelText="Keep Editing"
            />
        </FormProvider>
    );
};
